import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import type { ProgramItem } from "./TripDetail";
import TripDocuments from "../components/TripDocuments";
import Timeline from "../components/Timeline";
import ParticipantFinanceOverview from "../components/accounting/ParticipantFinanceOverview";

interface Trip {
  id: string;
  name: string;
  description: string;
  destination: string;
  start_date: string;
  end_date: string;
  is_public: boolean;
  max_participants: number | null;
  cover_image_url?: string;
  creator_id: string;
}

interface Participant {
  user_id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export default function TripView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [programItems, setProgramItems] = useState<ProgramItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const { data: tripData, error } = await supabase
        .from("trips")
        .select("*")
        .eq("id", id!)
        .single();

      if (!tripData || error) return;

      setTrip(tripData as Trip);

      const { data: participantData } = await supabase
        .from("trip_participants")
        .select("user_id")
        .eq("trip_id", tripData.id);

      if (participantData && participantData.length > 0) {
        const userIds = participantData.map((p) => p.user_id).filter((id): id is string => id !== null);

        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, email, first_name, last_name")
          .in("id", userIds);

        if (profilesData) {
          setParticipants(
            userIds.map((uid) => {
              const profile = profilesData.find((p) => p.id === uid);
              return {
                user_id: uid,
                email: profile?.email,
                first_name: profile?.first_name,
                last_name: profile?.last_name,
              };
            })
          );
        }
      }

      const { data: programData } = await supabase
        .from("trip_program_items")
        .select("*")
        .eq("trip_id", tripData.id)
        .order("start_time", { ascending: true });

      setProgramItems((programData || []) as ProgramItem[]);
      setLoading(false);
    };

    if (id) fetchData();
  }, [id]);

  if (loading) return <div className="p-6">Lade Reise...</div>;
  if (!trip)
    return <div className="p-6 text-red-600">Reise nicht gefunden.</div>;

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6 bg-white rounded shadow">
      {trip.cover_image_url && (
        <img
          src={trip.cover_image_url}
          alt={trip.name}
          className="w-full h-64 object-cover rounded mb-4"
        />
      )}
      <h1 className="text-3xl font-bold text-indigo-700 mb-2">{trip.name}</h1>
      <p className="text-gray-600 mb-2">{trip.destination}</p>
      <p className="text-sm text-gray-500 mb-4">
        {trip.start_date} – {trip.end_date}
      </p>
      <p className="mb-4">{trip.description}</p>

      <h2 className="text-lg font-semibold mt-8 mb-2">📆 Programm</h2>
      <Timeline items={programItems} trip={trip} />

      <h2 className="text-lg font-semibold mt-8 mb-2">👥 Teilnehmer</h2>
      <ul className="list-disc list-inside text-sm text-gray-700 mb-6">
        {participants.map((p) => {
          const displayName = `${p.first_name ?? ""} ${
            p.last_name ?? ""
          }`.trim();
          return (
            <li key={p.user_id}>
              {displayName || p.email || "Unbekannter Nutzer"}
            </li>
          );
        })}
      </ul>

      <h2 className="text-lg font-semibold mt-8 mb-2">📎 Dokumente</h2>
      <TripDocuments
        tripId={trip.id}
        userId={user?.id ?? ""}
        programItems={programItems}
        tripCreatorId={trip.creator_id}
        isOrganizer={user?.id === trip.creator_id}
      />

      {trip && user && (
        <ParticipantFinanceOverview tripId={trip.id} userId={user.id} />
      )}
    </div>
  );
}
